import { create } from 'zustand';
import api from '../../services/api';
import { Merchant } from '../../types/onboarding/Merchant';
import { ResponseData } from '../../types/response/ResponseData';
import ResponseError from '../../types/response/ResponseError';

/**
 * The guided setup keeps its own copy of the merchants, so pages that reload that list (the Merchants tab) do not make
 * the setup flicker.
 */
export interface GuidedSetupStore {
    merchants: Merchant[];
    isLoading: boolean;
    isLoaded: boolean;
    loadError: ResponseError | undefined;
    /** Loads the merchants (GET organizations/{id}/merchants). Connected stores come with the organization in the session. */
    loadMerchants: (organizationId: string) => Promise<void>;
    reset: () => void;
}

export const initialState = {
    merchants: [] as Merchant[],
    isLoading: false,
    isLoaded: false,
    loadError: undefined,
};

export const useGuidedSetupStore = create<GuidedSetupStore>(set => ({
    ...initialState,

    // A reload keeps what was loaded before shown until the new data is in.
    loadMerchants: async (organizationId: string) => {
        set({ isLoading: true, loadError: undefined });

        try {
            const response = await api.get<ResponseData<Merchant[]>>(
                `organizations/${organizationId}/merchants`
            );

            set({ merchants: response.data ?? [], isLoading: false, isLoaded: true });
        } catch (error) {
            set({ isLoading: false, isLoaded: true, loadError: error as ResponseError });
        }
    },

    reset: () => set(initialState),
}));
